import type { TourType } from '@/types/tour';
import { cn } from '@/utils/utils';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTour } from './TourContext';
import { tourConfig } from './tourConfig';
import type { TourStep } from './types';

interface TourOverlayProps {
  step: TourStep;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  tourId: TourType;
}

export const TourOverlay = memo(
  ({ step, onNext, tourId, onPrev }: TourOverlayProps) => {
    const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
    const [tooltipPosition, setTooltipPosition] = useState<{
      top: number;
      left: number;
    } | null>(null);
    const [isVisible, setIsVisible] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);

    const prevTargetExistsRef = useRef<boolean>(false);
    const hasAnimatedRef = useRef<string>('');

    const { currentStep: currentStepIndex, checkTourVisibility } = useTour();
    const tour = tourConfig[tourId];
    const totalSteps = tour?.steps.length ?? 0;

    const isElementInViewport = useCallback((rect: DOMRect) => {
      const buffer = 50;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      return (
        rect.top < viewportHeight + buffer &&
        rect.bottom > -buffer &&
        rect.left < viewportWidth + buffer &&
        rect.right > -buffer
      );
    }, []);

    // Enhanced position calculation with better anchoring
    const calculatePosition = useCallback(
      (targetElement: Element, tooltipWidth: number) => {
        const targetRect = targetElement.getBoundingClientRect();
        const margin = 12;
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const scrollLeft =
          window.pageXOffset || document.documentElement.scrollLeft;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const tooltipHeight = 180;

        // Get the target's absolute position
        const absoluteRect = {
          top: targetRect.top + scrollTop,
          left: targetRect.left + scrollLeft,
          width: targetRect.width,
          height: targetRect.height,
          bottom: targetRect.bottom + scrollTop,
          right: targetRect.right + scrollLeft,
        } as DOMRect;

        const targetAnchorPosition = step.targetAnchorPosition ?? 0.5;
        const tooltipAnchorPosition = step.tooltipAnchorPosition ?? 0.5;
        const xOffset = step.tooltipOffset?.x ?? 0;
        const yOffset = step.tooltipOffset?.y ?? 0;

        if (step.placement === 'bottom') {
          // Calculate base position centered on target
          let baseLeft =
            absoluteRect.left +
            absoluteRect.width * targetAnchorPosition -
            tooltipWidth * tooltipAnchorPosition;

          // Apply offset
          baseLeft += xOffset;

          // Ensure tooltip stays within viewport bounds
          if (baseLeft + tooltipWidth > scrollLeft + viewportWidth - margin) {
            baseLeft = scrollLeft + viewportWidth - tooltipWidth - margin;
          }
          if (baseLeft < scrollLeft + margin) {
            baseLeft = scrollLeft + margin;
          }

          // Calculate vertical position
          const baseTop = absoluteRect.bottom + margin + yOffset;

          return {
            top: baseTop,
            left: baseLeft,
          };
        }

        if (step.placement === 'right') {
          // For right placement, position tooltip to the right of the target
          const targetCenterY = absoluteRect.top + absoluteRect.height / 2;
          let tooltipTop =
            targetCenterY - tooltipHeight * tooltipAnchorPosition;
          const tooltipLeft = absoluteRect.right + margin + xOffset;

          // Ensure tooltip stays within viewport bounds vertically
          if (tooltipTop < scrollTop + margin) {
            tooltipTop = scrollTop + margin;
          }
          if (
            tooltipTop + tooltipHeight >
            scrollTop + viewportHeight - margin
          ) {
            tooltipTop = scrollTop + viewportHeight - tooltipHeight - margin;
          }

          return {
            top: tooltipTop,
            left: tooltipLeft,
          };
        }

        // Default fallback position
        return {
          top: absoluteRect.bottom + margin + yOffset,
          left: absoluteRect.left + xOffset,
        };
      },
      [
        step.placement,
        step.targetAnchorPosition,
        step.tooltipAnchorPosition,
        step.tooltipOffset,
      ],
    );

    const updatePosition = useCallback(() => {
      const target = document.querySelector(step.target);
      const targetExists = !!target;

      // Check if target existence changed
      if (targetExists !== prevTargetExistsRef.current) {
        prevTargetExistsRef.current = targetExists;
        // Notify context about visibility change
        checkTourVisibility();
      }

      if (!target) {
        // Target doesn't exist, hide the overlay
        setTargetRect(null);
        setTooltipPosition(null);
        setIsVisible(false);
        return;
      }

      const rect = target.getBoundingClientRect();
      const inViewport = isElementInViewport(rect);
      setIsVisible(inViewport);

      if (!inViewport) return;

      const tooltipWidth = 280;

      const position = calculatePosition(target, tooltipWidth);
      setTargetRect(rect);
      setTooltipPosition(position);
    }, [
      step.target,
      calculatePosition,
      isElementInViewport,
      checkTourVisibility,
    ]);

    // Handle initial positioning and animation - only when step actually changes
    useEffect(() => {
      const stepKey = `${tourId}-${step.id}`;

      // Only animate if this is a new step
      if (hasAnimatedRef.current !== stepKey) {
        hasAnimatedRef.current = stepKey;

        // Reset state when step changes to ensure fresh positioning
        setTargetRect(null);
        setTooltipPosition(null);
        setIsVisible(false);
        setIsAnimating(true);

        // Reset visibility tracking
        prevTargetExistsRef.current = false;

        const timer = setTimeout(() => {
          setIsVisible(true);
          setIsAnimating(false);
          updatePosition(); // Force position update with new step
        }, 50);

        return () => clearTimeout(timer);
      }

      // Just update position without animation
      updatePosition();
    }, [tourId, step.id, updatePosition]);

    // Enhanced scroll and resize handling with debounce
    useEffect(() => {
      let timeoutId: NodeJS.Timeout;
      let animationFrameId: number;
      let resizeObserver: ResizeObserver | null = null;

      const handleUpdate = () => {
        clearTimeout(timeoutId);
        cancelAnimationFrame(animationFrameId);

        animationFrameId = requestAnimationFrame(() => {
          timeoutId = setTimeout(() => {
            updatePosition();
          }, 16); // ~60fps
        });
      };

      const target = document.querySelector(step.target);
      if (target) {
        resizeObserver = new ResizeObserver(handleUpdate);
        resizeObserver.observe(target);

        const parentElement = target.parentElement;
        if (parentElement) {
          resizeObserver.observe(parentElement);
        }
      }

      // Watch for any DOM mutations that might affect positioning
      const mutationObserver = new MutationObserver(handleUpdate);
      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'width', 'height'],
      });

      window.addEventListener('scroll', handleUpdate, { passive: true });
      window.addEventListener('resize', handleUpdate);

      // Initial position update
      updatePosition();

      return () => {
        clearTimeout(timeoutId);
        cancelAnimationFrame(animationFrameId);
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
        mutationObserver.disconnect();
        window.removeEventListener('scroll', handleUpdate);
        window.removeEventListener('resize', handleUpdate);
      };
    }, [updatePosition, step.target]);

    // Don't render if target doesn't exist or positioning failed
    if (!targetRect || !tooltipPosition || !isVisible) return null;

    // Enhanced connection line calculation
    const getConnectionPoint = () => {
      const targetAnchorPosition = step.targetAnchorPosition ?? 0.5;
      const tooltipWidth = 280;

      if (step.placement === 'bottom') {
        // Calculate the x position that will be used for both points
        const connectionX =
          targetRect.left + targetRect.width * targetAnchorPosition;

        return {
          targetX: connectionX,
          targetY: targetRect.bottom - 2,
          tooltipX: connectionX,
          tooltipY: tooltipPosition.top,
        };
      }

      if (step.placement === 'right') {
        // Calculate the y position that will be used for both points
        const connectionY =
          targetRect.top + targetRect.height * targetAnchorPosition;

        return {
          targetX: targetRect.right,
          targetY: connectionY,
          tooltipX: tooltipPosition.left,
          tooltipY: connectionY,
        };
      }

      if (step.placement === 'left') {
        // Calculate the y position that will be used for both points
        const connectionY =
          targetRect.top + targetRect.height * targetAnchorPosition;

        return {
          targetX: targetRect.left,
          targetY: connectionY,
          tooltipX: tooltipPosition.left + tooltipWidth,
          tooltipY: connectionY,
        };
      }

      // Default case
      const targetX = targetRect.left + targetRect.width * targetAnchorPosition;
      const tooltipX =
        tooltipPosition.left + tooltipWidth * targetAnchorPosition;

      return {
        targetX,
        targetY: targetRect.bottom,
        tooltipX,
        tooltipY: tooltipPosition.top,
      };
    };

    const connectionPoint = getConnectionPoint();

    return (
      <>
        <svg
          className={`fixed inset-0 pointer-events-none z-50 transition-all duration-300 ease-out text-popover-elevated ${
            isAnimating ? 'hidden' : 'block'
          }`}
          style={{
            width: '100vw',
            height: '100vh',
          }}
        >
          <path
            d={`M ${connectionPoint.targetX} ${connectionPoint.targetY} L ${connectionPoint.tooltipX} ${connectionPoint.tooltipY}`}
            stroke="currentColor"
            strokeWidth="2"
            fill="none"
          />
          <circle
            cx={connectionPoint.targetX}
            cy={connectionPoint.targetY}
            r="4"
            fill="currentColor"
          />
        </svg>

        <div
          className={`fixed z-50 bg-popover-elevated text-popover rounded-lg p-4 shadow-md pointer-events-auto transition-all duration-300 ease-out transform ${
            isAnimating ? 'scale-95 translate-y-2' : 'scale-100 translate-y-0'
          }`}
          style={{
            top: tooltipPosition.top,
            left: tooltipPosition.left,
            width: '280px',
          }}
        >
          <div className="space-y-6">
            <div className="space-y-2.5">
              <h3 className="text-sm leading-[100%] dark:text-foreground text-background font-semibold">
                {step.title}
              </h3>
              <p className="dark:text-foreground text-background text-sm font-medium">
                {step.content}
              </p>
            </div>

            <div
              className={cn(
                'flex flex-row justify-between items-center',
                totalSteps < 2 && 'justify-end',
              )}
            >
              {totalSteps > 1 && (
                <div className="flex justify-center gap-[5px]">
                  {tour?.steps.map((tourStep, index) => (
                    <div
                      key={`${tourId}-${tourStep.id}-dot`}
                      className={`w-[5px] h-[5px] rounded-full transition-colors duration-200 ${
                        index === currentStepIndex
                          ? 'bg-background dark:bg-foreground'
                          : 'bg-muted-foreground/70'
                      }`}
                    />
                  ))}
                </div>
              )}

              <div className="flex justify-end items-center gap-3">
                {currentStepIndex > 0 && (
                  <button
                    className="text-sm font-semibold text-muted-foreground"
                    onClick={onPrev}
                  >
                    Back
                  </button>
                )}

                <button
                  className="text-sm font-semibold text-background dark:text-popover-foreground"
                  onClick={onNext}
                >
                  {currentStepIndex === totalSteps - 1 ? 'Close' : 'Next'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  },
);
